/**
 * Lesson nudges: which of a teacher's lessons still need an answer from them.
 * Pure: no React, no Firestore, no Date.now(). Callers pass `nowMs` and the
 * data; every nudge is reproducible in a test.
 *
 * Anthony, 2026-09-29: teachers were not starting lessons or marking
 * attendance, and he wanted "modal window popups that continue to render until
 * it's pressed", with no snooze. So the popup is driven entirely by the
 * record: a row disappears because the lesson now has an answer on file, never
 * because someone dismissed it. That is also what makes it clear on every
 * device at once.
 *
 * TWO KINDS, AND WHY NOT THREE. He asked about starting, completing and
 * attendance. Completing a lesson writes one fact that matters beyond the
 * timer, and it is attendance (useLessonTimer.complete fills `attended`). So
 * once the lesson is over, the question the popup asks IS attendance, and a
 * lesson with an outcome on file is answered whether or not Complete was ever
 * tapped. Its lesson page still says "Attended · not completed", which is
 * information, not a chore.
 *
 *   start       today, from START_AFTER_MS past the scheduled start until
 *               the end + ATTENDANCE_AFTER_MS, with no start recorded and no
 *               outcome. Buttons: Start now · Student didn't come.
 *   attendance  ATTENDANCE_AFTER_MS past the scheduled end (or any earlier day
 *               in the window), with no outcome. Buttons: Attended · No-show
 *               · Family cancelled.
 *
 * WHOSE LESSON. Earlier days read the teacher stamped on the record by the
 * nightly materializer (functions/lessonTeacher.js). Today's lessons have no
 * record or no stamp yet, so they resolve through the released rotation, the
 * same per-lesson-then-day rule the Today page uses (rotationLookup.js). A
 * lesson that resolves to nobody is never shown to anybody: naming a lesson to
 * the wrong teacher is worse than missing a nudge, and the nightly stamp logs
 * unresolved lessons by name for an admin.
 */

import { teacherIdForOccurrence, teacherIdFromRecord } from './rotationLookup';

export const NUDGE = Object.freeze({ START: 'start', ATTENDANCE: 'attendance' });

/** A start is only nagged about once the lesson is this late. */
export const START_AFTER_MS = 5 * 60 * 1000;

/** Attendance is asked this long after the scheduled end: time to wrap up. */
export const ATTENDANCE_AFTER_MS = 15 * 60 * 1000;

/** How far back unanswered lessons are shown, today included. Older ones are
 *  an admin's list, not a popup: the first popup must not be a wall. */
export const LOOKBACK_DAYS = 7;

const DEFAULT_DURATION = 45;
const MIN = 60 * 1000;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^\d{1,2}:\d{2}$/;

/** "2026-09-29" shifted by `days`, by local calendar parts (never UTC parse). */
export function addDays(dateStr, days) {
	const [y, m, d] = dateStr.split('-').map(Number);
	const dt = new Date(y, m - 1, d + days);
	return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
}

/** Studio-local instant for a date + "HH:MM". Runs in the teacher's browser,
 *  in the studio's timezone, so the local constructor is right here (see the
 *  same note in lessonTimerModel.scheduledStartMs). */
export function startMs(dateStr, time) {
	if (!DATE_RE.test(dateStr || '') || !TIME_RE.test(time || '')) return null;
	const [y, m, d] = dateStr.split('-').map(Number);
	const [h, mi] = time.split(':').map(Number);
	return new Date(y, m - 1, d, h, mi, 0, 0).getTime();
}

const minutesBetween = (a, b) => {
	if (!TIME_RE.test(a || '') || !TIME_RE.test(b || '')) return null;
	const [ah, am] = a.split(':').map(Number);
	const [bh, bm] = b.split(':').map(Number);
	const diff = (bh * 60 + bm) - (ah * 60 + am);
	return diff > 0 ? diff : null;
};

const isDone = (rec) => !!rec && (rec.status === 'completed' || rec.completedAt != null);

/** Which nudge, if any, one lesson owes right now. */
export function nudgeKindFor({ record, dateStr, todayStr, time, duration, nowMs }) {
	if (isDone(record)) return null;
	if (record && record.outcome) return null;
	const start = startMs(dateStr, time);
	if (start == null) return null;
	const end = start + (duration || DEFAULT_DURATION) * MIN;
	if (dateStr < todayStr) return NUDGE.ATTENDANCE;
	if (dateStr > todayStr) return null;
	if (nowMs >= end + ATTENDANCE_AFTER_MS) return NUDGE.ATTENDANCE;
	if (nowMs >= start + START_AFTER_MS && !(record && record.startedAt)) return NUDGE.START;
	return null;
}

/**
 * @param {object}   args
 * @param {string}   args.myTeacherId        users.teacherId of the viewer
 * @param {string}   args.todayStr           studio date, "YYYY-MM-DD"
 * @param {number}   args.nowMs
 * @param {object[]} args.todayOccurrences   today's LESSON occurrences (expandOnto
 *                                           + lessonOccurrence), test students removed
 * @param {object[]} args.records            `lessons` docs dated within the window
 * @param {object}   args.rotation           buildRotationLookup() result, or null
 * @param {Set}      [args.excludedStudentIds] test students
 * @returns {object[]} late starts first (they are happening NOW), then the
 *   attendance questions oldest first. Found on the first screenshot: a late
 *   start sorted by date sat below the fold under last week's lessons.
 */
export function buildLessonNudges({
	myTeacherId,
	todayStr,
	nowMs,
	todayOccurrences = [],
	records = [],
	rotation = null,
	excludedStudentIds = new Set(),
}) {
	if (!myTeacherId || !DATE_RE.test(todayStr || '')) return [];
	const fromStr = addDays(todayStr, -(LOOKBACK_DAYS - 1));
	const out = [];

	// Records by occurrence, the join the Today page uses (record.origin).
	const byOccurrence = new Map();
	records.forEach((r) => {
		const o = r.origin || {};
		if (o.slotId && o.originDate) byOccurrence.set(`${o.slotId}__${o.originDate}`, r);
	});

	// ---- Today: occurrences, joined to their record when one exists.
	const joined = new Set();
	todayOccurrences.forEach((o) => {
		if (!o || o._kind !== 'lesson' || excludedStudentIds.has(o.studentId)) return;
		const record = byOccurrence.get(`${o._slotId}__${o._originDate}`) || null;
		if (record) joined.add(record.id);
		const teacherId = teacherIdFromRecord(record) || (rotation ? teacherIdForOccurrence(rotation, o, todayStr) : null);
		if (!teacherId || teacherId !== myTeacherId) return;
		const duration = minutesBetween(o.startTime, o.endTime) || DEFAULT_DURATION;
		const kind = nudgeKindFor({ record, dateStr: todayStr, todayStr, time: o.startTime, duration, nowMs });
		if (!kind) return;
		out.push({
			key: record ? record.id : `occ_${o._slotId}_${o._originDate}`,
			kind,
			date: todayStr,
			time: o.startTime,
			duration,
			studentName: o.studentName || 'Student',
			location: o.location || null,
			// The shape setLessonOutcome / ensureLessonForOccurrence take.
			row: {
				lessonDocId: record ? record.id : null,
				slotId: o._slotId,
				originDate: o._originDate,
				time: o.startTime,
				duration,
				studentId: o.studentId || null,
				studentName: o.studentName || null,
				instrument: o.instrument || null,
				location: o.location || null,
				isMakeup: !!o.isMakeup,
			},
		});
	});

	// ---- Earlier days: the nightly materializer wrote a record for every
	// lesson and stamped its teacher, so records alone are the whole picture.
	records.forEach((r) => {
		if (joined.has(r.id)) return;
		if (!r.date || r.date < fromStr || r.date >= todayStr) return;
		if (excludedStudentIds.has(r.studentId)) return;
		if (teacherIdFromRecord(r) !== myTeacherId) return;
		const duration = r.duration || DEFAULT_DURATION;
		const kind = nudgeKindFor({ record: r, dateStr: r.date, todayStr, time: r.time, duration, nowMs });
		if (!kind) return;
		out.push({
			key: r.id,
			kind,
			date: r.date,
			time: r.time,
			duration,
			studentName: r.studentName || 'Student',
			location: r.location || null,
			row: {
				lessonDocId: r.id,
				slotId: (r.origin && r.origin.slotId) || null,
				originDate: (r.origin && r.origin.originDate) || null,
				time: r.time,
				duration,
				studentId: r.studentId || null,
				studentName: r.studentName || null,
				instrument: r.instrument || null,
				location: r.location || null,
				isMakeup: !!r.isMakeup,
			},
		});
	});

	const rank = (n) => (n.kind === NUDGE.START ? 0 : 1);
	return out.sort((a, b) => rank(a) - rank(b)
		|| a.date.localeCompare(b.date)
		|| (a.time || '').localeCompare(b.time || ''));
}
